import { useFetcher, useSearchParams } from '@remix-run/react';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FacetFilterTracker } from '~/components/facet-filter/facet-filter-tracker';
import FacetFilterControls from '~/components/facet-filter/FacetFilterControls';
import { NoResultsHint } from '~/components/products/NoResultsHint';
import { ProductCard } from '~/components/products/ProductCard';
import { ProductListCard } from '~/components/products/ProductListCard';
import { filteredSearchLoaderFromPagination } from '~/utils/filtered-search-loader';

export function InfiniteProductGrid({
    result,
    resultWithoutFacetValueFilters,
    facetValueIds,
    appliedPaginationLimit,
    mobileFiltersOpen,
    setMobileFiltersOpen,
    collectionSlug,
    term,
    hideFilters = false,
    viewMode,
}: Awaited<
    ReturnType<
        ReturnType<
            typeof filteredSearchLoaderFromPagination
        >['filteredSearchLoader']
    >
> & {
    mobileFiltersOpen: boolean;
    setMobileFiltersOpen: (arg0: boolean) => void;
    collectionSlug?: string;
    hideFilters?: boolean;
    viewMode: 'grid' | 'list';
}) {
    const { t } = useTranslation();
    const fetcher = useFetcher();
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const facetValuesTracker = useRef(new FacetFilterTracker());

    // Track all loaded products - use any[] to avoid type issues
    const [allProducts, setAllProducts] = useState<any[]>(result.items);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(result.items.length < result.totalItems);

    facetValuesTracker.current.update(
        result,
        resultWithoutFacetValueFilters,
        facetValueIds,
    );

    const hasFilters = facetValuesTracker.current?.facetsWithValues?.length > 0;

    // Reset products when collection changes or search term changes
    useEffect(() => {
        setAllProducts(result.items);
        setCurrentPage(1);
        setHasMore(result.items.length < result.totalItems);
    }, [collectionSlug, term, result.items, result.totalItems]);

    // Handle fetcher data when it returns
    useEffect(() => {
        if (fetcher.data && fetcher.state === 'idle') {
            const newResult = (fetcher.data as any).result;
            if (newResult?.items?.length > 0) {
                setAllProducts(prev => [...prev, ...newResult.items]);
                setHasMore(allProducts.length + newResult.items.length < newResult.totalItems);
            } else {
                setHasMore(false);
            }
        }
    }, [fetcher.data, fetcher.state]);

    const [searchParams] = useSearchParams();

    // Load more function
    const loadMore = useCallback(() => {
        if (fetcher.state !== 'idle' || !hasMore) return;

        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);

        // Build the URL with pagination params
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', nextPage.toString());
        newSearchParams.set('limit', appliedPaginationLimit.toString());

        // Ensure we keep existing filters (already in searchParams)
        // But we need to make sure we use the correct collection/search endpoint
        const baseUrl = collectionSlug ? `/collections/${collectionSlug}` : '/search';
        fetcher.load(`${baseUrl}?${newSearchParams.toString()}`);
    }, [fetcher, hasMore, currentPage, appliedPaginationLimit, collectionSlug, searchParams]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && fetcher.state === 'idle') {
                    loadMore();
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => observer.disconnect();
    }, [loadMore, hasMore, fetcher.state]);

    const isLoading = fetcher.state === 'loading';

    return (
        <div className="mt-6 w-full">
            {hasFilters && !hideFilters ? <FacetFilterControls
                facetFilterTracker={facetValuesTracker?.current}
                mobileFiltersOpen={mobileFiltersOpen}
                setMobileFiltersOpen={setMobileFiltersOpen}
            /> : null}

            {allProducts.length > 0 ? (
                <div className="space-y-6 w-full">
                    {/* Product Grid / List */}
                    <div className={viewMode === 'grid'
                        ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"
                        : "grid grid-cols-1 md:grid-cols-2 gap-4"
                    }>
                        {allProducts.map((item, index) => (
                            viewMode === 'grid' ? (
                                <ProductCard key={`${item.productId}-${index}`} {...item} />
                            ) : (
                                <ProductListCard key={`${item.productId}-${index}`} {...item} />
                            )
                        ))}
                    </div>

                    {/* Loading indicator */}
                    <div ref={loadMoreRef} className="flex justify-center py-8">
                        {isLoading ? (
                            <div className="flex flex-col items-center gap-2 text-gray-500">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                <span className="text-sm">Loading...</span>
                            </div>
                        ) : hasMore ? (
                            <div className="h-4" />
                        ) : (
                            <span className="text-gray-400 text-sm">
                                {t('product.showing')} {allProducts.length} / {result.totalItems}
                            </span>
                        )}
                    </div>
                </div>
            ) : (
                <NoResultsHint
                    facetFilterTracker={facetValuesTracker.current}
                    className={hasFilters ? 'lg:col-span-4 sm:p-4' : 'w-full sm:p-4'}
                />
            )}
        </div>
    );
}

